import type { Atom } from "@pdfking/tfunc"
import type { IterableLike } from "../types/IterableLike"
import { Items } from "@pdfking/tfunc"
import { is_mapping } from "./is_mapping"
import { ordered_object } from "./ordered_object"
import { to_array } from "./to_array"

/**
 * Maps function over iterable.
 *
 * For objects, returns mapped object. For arrays, returns mapped array.
 * Function receives (value, key/index).
 *
 * @param f - Mapping function
 * @param iterable - Array, object, or primitive to map
 * @returns Mapped result matching input type
 *
 * @example
 * ```ts
 * import { map } from "@pdfking/rfunc"
 *
 * map(v => v + 2, [1, 2, 3]) // [3, 4, 5]
 * map(v => v + 2, { a: 3, b: 5 }) // { a: 5, b: 7 }
 * ```
 */
export function map<T extends Atom | null | undefined, U>(
  f: (value: T, key: number) => U,
  iterable: T
): U[]
export function map<T, U>(f: (value: T, key: number) => U, iterable: T[]): U[]
export function map<T, U>(
  f: (value: T, key: number) => U,
  iterable: Iterable<T>
): U[]
export function map<K extends string, T, U>(
  f: (value: T, key: K) => U,
  iterable: Record<K, T>
): { [P in K]: U }
export function map<T, U>(
  f: (value: T, key: never) => U,
  iterable: IterableLike<T>
): U[] | Record<string, U> {
  if (is_mapping(iterable)) {
    return ordered_object(
      Items(iterable).map(a => [a[0], f(a[1] as T, a[0] as never)]) as [
        string,
        U,
      ][]
    )
  }
  return to_array(iterable).map((v, i) => f(v, i as never))
}
